// @ts-nocheck
/**
 * BureauDetailView — Salesforce-style Credit Bureau record detail.
 *
 * Renders a full-page detail view with collapsible sections:
 * - Information (Credit Bureau ID, Owner, Client, Stage, Authority, Portal Creation Date, Bureau Name)
 * - Unblock Date Information (Start Date, End Date, Perm Unblock Date)
 * - Login Information (User Name, PIN(s) Mailed, Password, PIN, Challenge Question, 4 Digit PIN, Challenge Answer)
 * - API Information (Request)
 * - Right sidebar: Notes (0), Bureau API Requests (0)
 *
 * All fields have inline pencil-edit icons matching the SF UI.
 */
import React, { useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Edit3,
  Plus,
  Save,
  X,
  Eye,
  EyeOff,
  StickyNote,
  FileText,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import type { BureauCredential } from '../../types';
import { formatAuditUserDate } from '../../utils/creditBlockFormatters';
import { getBureauLogo } from '../../constants/bureauLogos';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface BureauDetailViewProps {
  bureauKey: string;
  bureauLabel: string;
  credential: BureauCredential;
  clientName: string;
  accountOwner: string;
  onBack: () => void;
  onSaveField?: (bureauKey: string, fieldKey: string, value: string | boolean) => void;
}

/* ------------------------------------------------------------------ */
/*  Section wrapper (Salesforce collapsible section)                    */
/* ------------------------------------------------------------------ */

const SfSection: React.FC<{
  title: string;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}> = ({ title, defaultCollapsed = false, children }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 text-left hover:bg-gray-150 dark:hover:bg-gray-700/80 transition-colors"
      >
        {collapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        )}
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
      </button>
      {!collapsed && (
        <div className="px-4 py-4">
          {children}
        </div>
      )}
      {/* Salesforce-style coloured bottom stripe */}
      {!collapsed && <div className="h-1 bg-blue-500" />}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Inline editable field                                              */
/* ------------------------------------------------------------------ */

const SfField: React.FC<{
  label: string;
  value: string | undefined;
  fieldKey: string;
  type?: 'text' | 'date' | 'password' | 'checkbox' | 'select' | 'textarea';
  selectOptions?: { value: string; label: string }[];
  onSave?: (fieldKey: string, value: string | boolean) => void;
  showPassword?: boolean;
  onTogglePassword?: () => void;
}> = ({
  label,
  value,
  fieldKey,
  type = 'text',
  selectOptions,
  onSave,
  showPassword,
  onTogglePassword,
}) => {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value ?? '');

  const startEdit = () => {
    setTempValue(value ?? '');
    setEditing(true);
  };

  const save = () => {
    const saveValue = type === 'checkbox' ? tempValue === 'true' : tempValue;
    onSave?.(fieldKey, saveValue);
    setEditing(false);
  };

  const cancel = () => setEditing(false);
  const empty = <span className="text-gray-400 dark:text-gray-500 italic">—</span>;

  if (editing) {
    return (
      <div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</div>
        <div className="flex items-center gap-1">
          {type === 'checkbox' ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={tempValue === 'true'}
                onChange={(e) => setTempValue(e.target.checked ? 'true' : '')}
              />
              Yes
            </label>
          ) : type === 'select' ? (
            <select
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              autoFocus
            >
              <option value="">—</option>
              {selectOptions?.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : type === 'textarea' ? (
            <textarea
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              rows={3}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              autoFocus
            />
          ) : (
            <input
              type={type === 'password' && !showPassword ? 'password' : type === 'password' ? 'text' : type}
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              autoFocus
            />
          )}
          <button type="button" onClick={save} className="p-1 text-emerald-600 hover:text-emerald-700" title="Save">
            <Save className="h-4 w-4" />
          </button>
          <button type="button" onClick={cancel} className="p-1 text-gray-500 hover:text-gray-600" title="Cancel">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Display mode
  let displayValue: React.ReactNode;
  if (type === 'checkbox') {
    displayValue = value === 'true' || value === true ? (
      <span className="inline-block w-4 h-4 border border-gray-400 rounded bg-blue-600 text-white text-center leading-4 text-xs">✓</span>
    ) : (
      <span className="inline-block w-4 h-4 border border-gray-400 dark:border-gray-500 rounded" />
    );
  } else if (type === 'password') {
    displayValue = value ? (showPassword ? value : '••••••••') : empty;
  } else if (type === 'date') {
    if (!value) {
      displayValue = empty;
    } else {
      const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const d = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(value);
      displayValue = Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
    }
  } else {
    displayValue = value || empty;
  }

  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
      <div className="group flex items-center gap-2 min-h-[1.75rem]">
        <div className="text-sm text-gray-900 dark:text-gray-100 flex-1">{displayValue}</div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {type === 'password' && value && onTogglePassword && (
            <button type="button" onClick={onTogglePassword} className="p-0.5 text-gray-400 hover:text-gray-600" title={showPassword ? 'Hide' : 'Show'}>
              {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          )}
          {onSave && (
            <button type="button" onClick={startEdit} className="p-0.5 text-gray-400 hover:text-blue-600" title="Edit">
              <Edit3 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Sidebar card                                                       */
/* ------------------------------------------------------------------ */

const SidebarCard: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  count: number;
  children?: React.ReactNode;
}> = ({ icon, iconBg, title, count, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className={`flex items-center justify-center w-6 h-6 rounded ${iconBg} text-white`}>
            {icon}
          </span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {title} ({count})
          </span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && children && <div className="border-t border-gray-200 dark:border-gray-700">{children}</div>}
    </div>
  );
};

const BureauNotesSection: React.FC<{ bureauNumericId: number }> = ({ bureauNumericId }) => {
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data: notes = [] } = useQuery({
    queryKey: ['credit-block', 'bureau-notes', bureauNumericId],
    queryFn: async () => {
      const client = getGobodoClient();
      const api = new GobodoApi.CommentsApi(client);
      const comments = await api.getComments(bureauNumericId);
      if (!Array.isArray(comments)) return [];
      return comments.map((c: any) => ({
        id: String(c.id || ''),
        body: String(c.body || c.content || ''),
        createdBy: String(c.creatorid || ''),
        createdDate: String(c.creationdate || ''),
      }));
    },
    enabled: shouldUseGobodo() && bureauNumericId > 0,
    staleTime: 60_000,
  });

  const addNote = async () => {
    const body = newNote.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      const client = getGobodoClient();
      const api = new GobodoApi.CommentsApi(client);
      const input = new GobodoApi.CommentInput({});
      input.setParentobjectid(bureauNumericId);
      input.setBody(body);
      input.setSubject('Bureau Note');
      await api.createComment(input);
      setNewNote('');
      queryClient.invalidateQueries({ queryKey: ['credit-block', 'bureau-notes', bureauNumericId] });
    } catch {
      // best-effort
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (v: string) => {
    if (!v) return '';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="px-4 py-3 space-y-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
          placeholder="Add a note..."
          className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        />
        <button
          type="button"
          onClick={addNote}
          disabled={!newNote.trim() || submitting}
          className="px-2.5 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      {notes.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No notes yet.</p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {notes.map((n) => (
            <div key={n.id} className="text-xs border-b border-gray-100 dark:border-gray-700 pb-2 last:border-0">
              <p className="text-gray-900 dark:text-gray-100">{n.body}</p>
              <p className="text-gray-400 dark:text-gray-500 mt-0.5">{fmtDate(n.createdDate)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const BureauDetailView: React.FC<BureauDetailViewProps> = ({
  bureauKey,
  bureauLabel,
  credential,
  clientName,
  accountOwner,
  onBack,
  onSaveField,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const cred = credential ?? {};
  const bureauNumericId = (() => {
    const raw = String(cred.creditBureauId ?? '').trim();
    if (/^\d+$/.test(raw)) return Number(raw);
    return 0;
  })();
  const requestNotesValue =
    typeof cred.requestNotes === 'string'
      ? cred.requestNotes
      : cred.requestNotes != null
        ? JSON.stringify(cred.requestNotes, null, 2)
        : '';
  const createdByDisplay = formatAuditUserDate(cred.createdBy, cred.createdDate);
  const lastModifiedByDisplay = formatAuditUserDate(cred.lastModifiedBy, cred.lastModifiedDate);

  const handleSave = (fieldKey: string, value: string | boolean) => {
    onSaveField?.(bureauKey, fieldKey, value);
  };

  return (
    <div className="space-y-4">
      {/* Back navigation */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Credit Bureaus
      </button>

      {/* Header */}
      {(() => {
        const logoSrc = getBureauLogo(bureauLabel);
        return (
          <div className="flex items-center gap-3 mb-2">
            {logoSrc ? (
              <span className="flex h-9 w-9 items-center justify-center rounded bg-white border border-gray-200 dark:border-gray-700 overflow-hidden">
                <img
                  src={logoSrc}
                  alt={`${bureauLabel} logo`}
                  className="h-7 w-7 object-contain"
                />
              </span>
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded bg-slate-600 text-white">
                <span className="text-xs font-bold">CB</span>
              </span>
            )}
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{bureauLabel}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Credit Bureau · {cred.creditBureauId || '—'}</p>
            </div>
          </div>
        );
      })()}

      {/* Two-column layout: main content + sidebar */}
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Information */}
          <SfSection title="Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <SfField label="Credit Bureau ID" value={cred.creditBureauId ? String(cred.creditBureauId) : ''} fieldKey="creditBureauId" />
              <SfField label="Owner" value={accountOwner} fieldKey="owner" />
              <SfField label="Client" value={clientName} fieldKey="client" />
              <SfField
                label="Stage"
                value={cred.status === 'locked' ? 'Blocked' : cred.status === 'unlocked' ? 'Unblocked' : cred.status === 'permanently_unblocked' ? 'Permanently Unblocked' : cred.status ?? ''}
                fieldKey="status"
                type="select"
                selectOptions={[
                  { value: 'unlocked', label: 'Unblocked' },
                  { value: 'locked', label: 'Blocked' },
                  { value: 'permanently_unblocked', label: 'Permanently Unblocked' },
                ]}
                onSave={handleSave}
              />
              <SfField
                label="Authority"
                value={cred.authority ?? ''}
                fieldKey="authority"
                type="select"
                selectOptions={[
                  { value: 'primary', label: 'Primary' },
                  { value: 'secondary', label: 'Secondary' },
                ]}
                onSave={handleSave}
              />
              <SfField label="Portal Creation Date" value={cred.portalCreationDate ?? ''} fieldKey="portalCreationDate" type="date" onSave={handleSave} />
              <SfField label="Bureau Name" value={bureauLabel} fieldKey="bureauName" />
            </div>
          </SfSection>

          {/* Unblock Date Information */}
          <SfSection title="Unblock Date Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <SfField label="Start Date" value={cred.startDate ?? cred.enrollmentDate ?? ''} fieldKey="startDate" type="date" onSave={handleSave} />
              <SfField label="Perm Unblock Date" value={cred.permUnblockDate ?? ''} fieldKey="permUnblockDate" type="date" onSave={handleSave} />
              <SfField label="End Date" value={cred.endDate ?? cred.unblockDate ?? ''} fieldKey="endDate" type="date" onSave={handleSave} />
            </div>
          </SfSection>

          {/* Login Information */}
          <SfSection title="Login Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <SfField label="User Name" value={cred.username ?? ''} fieldKey="username" onSave={handleSave} />
              <SfField label="PIN(s) Mailed" value={cred.pinMailed ? 'true' : ''} fieldKey="pinMailed" type="checkbox" onSave={handleSave} />
              <SfField
                label="Password"
                value={cred.password ?? ''}
                fieldKey="password"
                type="password"
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword((p) => !p)}
                onSave={handleSave}
              />
              <SfField label="PIN" value={cred.pin ?? ''} fieldKey="pin" onSave={handleSave} />
              <SfField label="Challenge Question" value={cred.challengeQuestion ?? ''} fieldKey="challengeQuestion" onSave={handleSave} />
              <SfField label="4 Digit PIN" value={cred.fourDigitPin ?? ''} fieldKey="fourDigitPin" onSave={handleSave} />
              <SfField label="Challenge Answer" value={cred.challengeAnswer ?? ''} fieldKey="challengeAnswer" onSave={handleSave} />
            </div>
          </SfSection>

          {/* Process Collection */}
          <SfSection title="Process Collection">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <SfField label="Block Process Collection" value={cred.blockProcessCollection ? 'true' : ''} fieldKey="blockProcessCollection" type="checkbox" onSave={handleSave} />
              <SfField label="Unblock Process Collection" value={cred.unblockProcessCollection ? 'true' : ''} fieldKey="unblockProcessCollection" type="checkbox" onSave={handleSave} />
            </div>
          </SfSection>

          {/* API Information */}
          <SfSection title="API Information">
            <div className="grid grid-cols-1 gap-y-4">
              <SfField label="Request" value={requestNotesValue} fieldKey="requestNotes" type="textarea" onSave={handleSave} />
            </div>
          </SfSection>

          {/* System Information */}
          <SfSection title="System Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              <SfField label="Created By" value={createdByDisplay} fieldKey="createdBy" />
              <SfField label="Last Modified By" value={lastModifiedByDisplay} fieldKey="lastModifiedBy" />
            </div>
          </SfSection>

        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-64 flex-shrink-0 space-y-3">
          <SidebarCard
            icon={<StickyNote className="w-3.5 h-3.5" />}
            iconBg="bg-red-500"
            title="Notes"
            count={0}
          >
            {bureauNumericId > 0 && <BureauNotesSection bureauNumericId={bureauNumericId} />}
          </SidebarCard>
          <SidebarCard
            icon={<FileText className="w-3.5 h-3.5" />}
            iconBg="bg-indigo-600"
            title="Bureau API Requests"
            count={0}
          />
        </div>
      </div>
    </div>
  );
};

export default BureauDetailView;
